import { HttpFailedToFetchException } from "../model/httpException";
import { logException } from "../utils/logUtils";
import {
  AppDetailResponse,
  AppSearchResponse,
  CategoriesResponse,
  CountriesResponse,
  DeviceType,
  RankingsAllResponse,
  StoreType,
} from "../model/rankings";

/**
 * Base URL for the rankings API (blackfinch.pl)
 * Used for API calls and resolving relative icon paths
 */
export const RANKINGS_API_BASE_URL = "https://blackfinch.pl";

/**
 * API layer for app rankings endpoints
 * Endpoint: https://blackfinch.pl/app-rankings
 */
export class RankingsApi {
  private static readonly BASE_ENDPOINT = `${RANKINGS_API_BASE_URL}/app-rankings`;

  /**
   * Fetch all rankings (free, paid, grossing) in one request
   */
  static async fetchRankings(
    store: StoreType,
    country: string,
    category?: string,
    num?: number,
    device?: DeviceType
  ): Promise<RankingsAllResponse> {
    const params = new URLSearchParams({
      store,
      country,
    });

    if (category) {
      params.set("category", category);
    }

    if (num) {
      params.set("num", num.toString());
    }

    // Add device param only for iOS
    if (store === "ios" && device) {
      params.set("device", device);
    }

    const response = await fetch(
      `${this.BASE_ENDPOINT}/rankings/all?${params}`,
      { method: "GET" }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException("Rankings API error (/api/rankings/all):", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    return response.json();
  }

  /**
   * Fetch available categories for a store
   */
  static async fetchCategories(store: StoreType): Promise<CategoriesResponse> {
    const response = await fetch(
      `${this.BASE_ENDPOINT}/categories?store=${store}`,
      { method: "GET" }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException("Rankings API error (/api/categories):", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    return response.json();
  }

  /**
   * Fetch available countries
   */
  static async fetchCountries(): Promise<CountriesResponse> {
    const response = await fetch(`${this.BASE_ENDPOINT}/countries`, {
      method: "GET",
    });

    if (!response.ok) {
      const errorText = await response.text();
      logException("Rankings API error (/api/countries):", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    return response.json();
  }

  /**
   * Fetch detailed app information
   */
  static async fetchAppDetail(
    store: StoreType,
    id: string,
    country?: string
  ): Promise<AppDetailResponse> {
    const params = new URLSearchParams({
      store,
      id,
    });

    if (country) {
      params.set("country", country);
    }

    const response = await fetch(`${this.BASE_ENDPOINT}/app?${params}`, {
      method: "GET",
    });

    if (!response.ok) {
      const errorText = await response.text();
      logException("Rankings API error (/api/app):", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    return response.json();
  }

  /**
   * Search for apps by name
   */
  static async searchApps(
    store: StoreType,
    term: string,
    country: string = "us"
  ): Promise<AppSearchResponse> {
    const params = new URLSearchParams({
      store,
      term,
      country,
    });

    const response = await fetch(`${this.BASE_ENDPOINT}/search?${params}`, {
      method: "GET",
    });

    if (!response.ok) {
      const errorText = await response.text();
      logException("Rankings API error (/api/search):", {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    return response.json();
  }
}
